import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { X, Loader2 } from 'lucide-react';
import { useDebounce } from '../hooks/useDebounce';
import { useSearchBooks } from '../hooks/useBooks';

interface GlobalSearchProps {
  mobileOpen?: boolean;
  onMobileClose?: () => void;
}

export function GlobalSearch({ mobileOpen, onMobileClose }: GlobalSearchProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [query, setQuery] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const debouncedQuery = useDebounce(query.trim(), 300);
  const wrapperRef = useRef<HTMLFormElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listboxId = 'global-search-results';

  const { data: results, isFetching } = useSearchBooks({
    q: debouncedQuery,
    limit: 6,
  });

  const showDropdown = isFocused && debouncedQuery.length > 0;
  const books = results ?? [];

  // Close dropdown on route change
  useEffect(() => {
    setIsFocused(false);
    setQuery('');
    setActiveIndex(-1);
  }, [location.pathname]);

  // Click outside to close
  useEffect(() => {
    if (!showDropdown) return;

    const handleClick = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setIsFocused(false);
      }
    };

    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showDropdown]);

  // Auto-focus when mobile overlay opens
  useEffect(() => {
    if (mobileOpen) {
      inputRef.current?.focus();
    }
  }, [mobileOpen]);

  const closeAndNavigate = useCallback(
    (path: string) => {
      setIsFocused(false);
      setQuery('');
      setActiveIndex(-1);
      onMobileClose?.();
      navigate(path);
    },
    [navigate, onMobileClose],
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const q = query.trim();
    if (q) {
      closeAndNavigate(`/books?q=${encodeURIComponent(q)}`);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setIsFocused(false);
      setActiveIndex(-1);
      inputRef.current?.blur();
      onMobileClose?.();
      return;
    }

    if (!showDropdown || books.length === 0) return;

    const maxIndex = books.length; // includes "See all results" footer

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((prev) => (prev < maxIndex ? prev + 1 : 0));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((prev) => (prev > 0 ? prev - 1 : maxIndex));
    } else if (e.key === 'Enter' && activeIndex >= 0) {
      e.preventDefault();
      const activeBook = books[activeIndex];
      if (activeBook) {
        closeAndNavigate(`/books/${activeBook.id}`);
      } else {
        // "See all results" footer
        closeAndNavigate(`/books?q=${encodeURIComponent(query.trim())}`);
      }
    }
  };

  const activeBook = activeIndex >= 0 && activeIndex < books.length ? books[activeIndex] : undefined;
  const activeDescendant =
    activeIndex >= 0
      ? activeBook
        ? `global-search-result-${activeBook.id}`
        : 'global-search-see-all'
      : undefined;

  const searchContent = (
    <form
      ref={wrapperRef}
      className={`global-search ${mobileOpen ? 'global-search--mobile-overlay' : ''}`}
      role="search"
      onSubmit={handleSubmit}
    >
      <div className="global-search__bar">
        <input
          ref={inputRef}
          type="search"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setActiveIndex(-1);
          }}
          onFocus={() => setIsFocused(true)}
          placeholder="Search books..."
          className="global-search__input"
          role="combobox"
          aria-label="Search books"
          aria-expanded={showDropdown && books.length > 0}
          aria-controls={listboxId}
          aria-activedescendant={activeDescendant}
          aria-autocomplete="list"
          onKeyDown={handleKeyDown}
        />
        {query && !isFetching && (
          <button
            type="button"
            onClick={() => {
              setQuery('');
              setActiveIndex(-1);
              inputRef.current?.focus();
            }}
            className="global-search__clear"
            aria-label="Clear search"
          >
            <X className="icon-sm" aria-hidden="true" />
          </button>
        )}
        {isFetching && (
          <Loader2 className="global-search__spinner animate-spin" aria-hidden="true" />
        )}
      </div>

      {showDropdown && (
        <div className="global-search__dropdown" id={listboxId} role="listbox" aria-label="Search results">
          {books.length > 0 ? (
            <>
              {books.map((book, index) => (
                <div
                  key={book.id}
                  id={`global-search-result-${book.id}`}
                  role="option"
                  aria-selected={activeIndex === index}
                  className={`global-search__result ${activeIndex === index ? 'global-search__result--active' : ''}`}
                  onMouseEnter={() => setActiveIndex(index)}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    closeAndNavigate(`/books/${book.id}`);
                  }}
                >
                  <span className="global-search__result-title">{book.title}</span>
                  {book.authors?.[0] && (
                    <span className="global-search__result-author">{book.authors[0].name}</span>
                  )}
                </div>
              ))}
              <div
                id="global-search-see-all"
                role="option"
                aria-selected={activeIndex === books.length}
                className={`global-search__footer ${activeIndex === books.length ? 'global-search__footer--active' : ''}`}
                onMouseEnter={() => setActiveIndex(books.length)}
                onMouseDown={(e) => {
                  e.preventDefault();
                  closeAndNavigate(`/books?q=${encodeURIComponent(query.trim())}`);
                }}
              >
                See all results
              </div>
            </>
          ) : !isFetching ? (
            <div className="global-search__empty">
              No results for &ldquo;{debouncedQuery}&rdquo;
            </div>
          ) : null}
        </div>
      )}

      <div className="sr-only" aria-live="polite" aria-atomic="true">
        {showDropdown && !isFetching && books.length > 0
          ? `${books.length} result${books.length !== 1 ? 's' : ''} found`
          : showDropdown && !isFetching && debouncedQuery
            ? 'No results found'
            : ''}
      </div>
    </form>
  );

  // Mobile overlay wraps the search in a fixed container
  if (mobileOpen) {
    return (
      <div className="global-search-overlay">
        <div className="global-search-overlay__header">
          {searchContent}
          <button
            type="button"
            className="global-search-overlay__close"
            onClick={onMobileClose}
            aria-label="Close search"
          >
            <X className="icon-md" aria-hidden="true" />
          </button>
        </div>
      </div>
    );
  }

  return searchContent;
}
